import React, { useRef } from 'react';
import AppDropdown from '../../../../components/AppDropdown';

export default function ProfilePersonalSection({
  formData = {},
  onChange,
  onPhotoUpload,
  onSave,
  isSaving,
}) {
  const fileInputRef = useRef(null);

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
  ];

  const bloodGroupOptions = [
    { value: 'A+', label: 'A+' },
    { value: 'A-', label: 'A-' },
    { value: 'B+', label: 'B+' },
    { value: 'B-', label: 'B-' },
    { value: 'O+', label: 'O+' },
    { value: 'O-', label: 'O-' },
    { value: 'AB+', label: 'AB+' },
    { value: 'AB-', label: 'AB-' },
  ];

  const maritalStatusOptions = [
    { value: 'Unmarried', label: 'Unmarried' },
    { value: 'Married', label: 'Married' },
    { value: 'Divorced', label: 'Divorced' },
  ];

  const handicappedOptions = [
    { value: 'No', label: 'No' },
    { value: 'Yes', label: 'Yes' },
  ];

  return (
    <form onSubmit={onSave} className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-xs overflow-hidden">
      {/* Header bar matching Screenshot 1 */}
      <div className="bg-primary/95 px-6 py-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-2.5">
          <span className="material-symbols-outlined text-xl">account_box</span>
          <h2 className="text-base font-bold tracking-wide">My Profile</h2>
        </div>
        <span className="text-xs font-semibold bg-white/15 px-3 py-1 rounded-lg">
          Employee ID: {formData.employeeId || '348414'}
        </span>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Photo Upload Column (Left) */}
          <div className="lg:col-span-3 flex flex-col items-center justify-center p-5 rounded-2xl bg-surface-container-low/50 border border-outline-variant/20 border-dashed">
            <div className="relative h-40 w-40 overflow-hidden rounded-2xl border-2 border-primary/20 bg-surface-container-lowest shadow-sm mb-4 flex items-center justify-center">
              {formData.avatarUrl ? (
                <img
                  src={formData.avatarUrl}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400">
                  <span className="material-symbols-outlined text-6xl text-slate-300">person</span>
                  <span className="text-[11px] font-semibold mt-1">No Image</span>
                </div>
              )}
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={onPhotoUpload}
              accept="image/*"
              className="hidden"
            />

            <div className="flex items-center gap-2 w-full">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 rounded-xl bg-surface-container-high px-3 py-2 text-xs font-bold text-slate-700 hover:bg-surface-container-highest transition cursor-pointer text-center"
              >
                Choose Photo
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2 text-center">
              JPG, PNG or WEBP up to 2MB
            </p>
          </div>

          {/* Form Fields Column (Right) */}
          <div className="lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {/* Gender */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Gender</label>
              <AppDropdown
                value={formData.gender || 'Male'}
                onChange={(val) => onChange('gender', val)}
                options={genderOptions}
                triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-semibold text-on-surface"
              />
            </div>

            {/* Blood Group */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Blood Group</label>
              <AppDropdown
                value={formData.bloodGroup || 'B+'}
                onChange={(val) => onChange('bloodGroup', val)}
                options={bloodGroupOptions}
                triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-semibold text-on-surface"
              />
            </div>

            {/* Mobile No */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Mobile No</label>
              <input
                type="tel"
                value={formData.mobile || ''}
                onChange={(e) => onChange('mobile', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Email-ID */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Email-ID</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => onChange('email', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* DOB */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">DOB (Date of Birth)</label>
              <input
                type="date"
                value={formData.dob || ''}
                onChange={(e) => onChange('dob', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Anniversary */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Anniversary</label>
              <input
                type="date"
                value={formData.anniversary || ''}
                onChange={(e) => onChange('anniversary', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Aadhar Number */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Aadhar Number</label>
              <input
                type="text"
                value={formData.aadharNumber || ''}
                onChange={(e) => onChange('aadharNumber', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Nationality */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Nationality</label>
              <input
                type="text"
                value={formData.nationality || 'Indian'}
                onChange={(e) => onChange('nationality', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Religion */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Religion</label>
              <input
                type="text"
                value={formData.religion || 'Hindu'}
                onChange={(e) => onChange('religion', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Caste */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Caste</label>
              <input
                type="text"
                value={formData.caste || ''}
                onChange={(e) => onChange('caste', e.target.value)}
                placeholder="General / OBC / SC / ST"
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Handicapped */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">HandiCapped</label>
              <AppDropdown
                value={formData.handicapped || 'No'}
                onChange={(val) => onChange('handicapped', val)}
                options={handicappedOptions}
                triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-semibold text-on-surface"
              />
            </div>

            {/* Height (cm) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Height (cm)</label>
              <input
                type="number"
                step="0.1"
                value={formData.height || ''}
                onChange={(e) => onChange('height', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Weight (KG) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Weight (KG)</label>
              <input
                type="number"
                step="0.1"
                value={formData.weight || ''}
                onChange={(e) => onChange('weight', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-sans tabular-nums font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Marital Status */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Marital Status</label>
              <AppDropdown
                value={formData.maritalStatus || 'Unmarried'}
                onChange={(val) => onChange('maritalStatus', val)}
                options={maritalStatusOptions}
                triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-semibold text-on-surface"
              />
            </div>

            {/* Company Experience */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Company Experience</label>
              <input
                type="text"
                value={formData.companyExperience || 'Fresher'}
                onChange={(e) => onChange('companyExperience', e.target.value)}
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Total Experience */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Total Experience</label>
              <input
                type="text"
                value={formData.totalExperience || ''}
                onChange={(e) => onChange('totalExperience', e.target.value)}
                placeholder="e.g. 2 Years"
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>

            {/* Remarks (Full width) */}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">Remarks</label>
              <textarea
                rows={2}
                value={formData.remarks || ''}
                onChange={(e) => onChange('remarks', e.target.value)}
                placeholder="Additional notes or profile remarks..."
                className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2.5 text-xs font-medium text-on-surface focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Save button bar matching Screenshot 1 */}
        <div className="mt-6 flex justify-end border-t border-outline-variant/15 pt-4">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer disabled:opacity-50"
          >
            <span className="material-symbols-outlined text-base">save</span>
            <span>{isSaving ? 'Saving...' : 'Save Profile'}</span>
          </button>
        </div>
      </div>
    </form>
  );
}
